import { useEffect, useRef, useState, type FormEvent } from 'react'
import {
  catalog,
  F,
  lensLabel,
  MAX_MEMBER_NUMBER_LENGTH,
  MAX_NAME_LENGTH,
  OFFERED_LENS_TYPES,
  rangeValues,
  signed,
  todayEt,
  type FieldError,
  type OrderForm,
} from '@glasses/shared'
import { Card } from '../components/Card'
import { ErrorText } from '../components/ErrorText'
import { submit } from '../lib/data'
import { clearDraft, keepDraft, readDraft } from '../lib/draft'
import { draftAfter, errorsAfter } from '../lib/submitFlow'
import { eyeErrors, newForm, toggleColor, toggleFrame } from '../lib/orderForm'
import { signInAgain } from '../lib/session'
import { FramePicker } from './FramePicker'
import { RxRow } from './RxRow'

/** What the last submit left to say beside the button (field errors are separate). */
type Outcome = null | { saveFailed: true } | { sessionExpired: true } | { rejected: string }

const num = (v: string): number | null => (v === '' ? null : Number(v))

function session(): Storage | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

/** A draft kept across a sign-in redirect wins over a blank form (and keeps its submission key). */
function initialForm(): OrderForm {
  const storage = session()
  return (storage && readDraft(storage)) ?? newForm(todayEt(new Date()))
}

export function NewOrder({ onSubmitted }: { onSubmitted: (orderId: string) => void }) {
  const [form, setForm] = useState<OrderForm>(initialForm)
  const [errors, setErrors] = useState<FieldError[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState<Outcome>(null)
  const inFlight = useRef(false)
  const mounted = useRef(true)
  const heading = useRef<HTMLHeadingElement>(null)
  const today = todayEt(new Date())

  useEffect(() => {
    mounted.current = true
    // The form state now holds any restored draft; storage must not restore it a second time.
    const storage = session()
    if (storage) clearDraft(storage)
    document.title = 'New glasses order'
    heading.current?.focus()
    return () => {
      // Leaving the page drops the in-flight result.
      mounted.current = false
    }
  }, [])

  const set = (patch: Partial<OrderForm>) => setForm((f) => ({ ...f, ...patch }))
  const list = errors ?? []

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setOutcome(null)
    const storage = session()
    // Kept first: a token renewal that has to leave the page mid-submit must not lose the form.
    if (storage) keepDraft(storage, form)
    let keepBusy = false
    try {
      const r = await submit(form)
      // Settled first, mounted or not: leaving mid-submit must not strand the PHI draft.
      if (storage && draftAfter(r) === 'clear') clearDraft(storage)
      if (!mounted.current) return
      setErrors(errorsAfter(r))
      if (r.ok) {
        keepBusy = true // stay disabled while navigating away
        onSubmitted(r.orderId)
        return
      }
      // Same form, same submission key: a retry that reaches a saved order returns it.
      // An expired session keeps the draft: "Sign in" leaves the page and the form comes back after.
      if ('sessionExpired' in r) setOutcome({ sessionExpired: true })
      else if ('rejected' in r) setOutcome({ rejected: r.message })
      else if ('saveFailed' in r) setOutcome({ saveFailed: true })
    } finally {
      if (!keepBusy) {
        inFlight.current = false
        if (mounted.current) setBusy(false)
      }
    }
  }

  return (
    <>
      <h1 ref={heading} tabIndex={-1}>
        New glasses order
      </h1>

      <form onSubmit={onSubmit}>
        <Card icon="person" title="Participant">
          <div className="field-grid">
            <div className="field">
              <label htmlFor="order-date">Order date</label>
              <input
                id="order-date"
                type="date"
                max={today}
                value={form.order_date ?? ''}
                onChange={(e) => set({ order_date: e.target.value || null })}
              />
              <ErrorText field={F.orderDate} errors={list} />
            </div>
            <div className="field">
              <label htmlFor="participant-name">Participant name</label>
              <input
                id="participant-name"
                maxLength={MAX_NAME_LENGTH}
                value={form.participant_name}
                onChange={(e) => set({ participant_name: e.target.value })}
              />
              <ErrorText field={F.participantName} errors={list} />
            </div>
            <div className="field">
              <label htmlFor="member-number">Member number</label>
              <input
                id="member-number"
                maxLength={MAX_MEMBER_NUMBER_LENGTH}
                value={form.member_number}
                onChange={(e) => set({ member_number: e.target.value })}
              />
              <ErrorText field={F.memberNumber} errors={list} />
            </div>
            <div className="field">
              <label htmlFor="center">Center</label>
              <select id="center" value={form.center_id ?? ''} onChange={(e) => set({ center_id: e.target.value || null })}>
                <option value="">Choose…</option>
                {catalog.centers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <ErrorText field={F.center} errors={list} />
            </div>
            <div className="field">
              <label htmlFor="birth-year">Birth year</label>
              <select
                id="birth-year"
                value={form.birth_year ?? ''}
                onChange={(e) => set({ birth_year: num(e.target.value) })}
              >
                <option value="">Choose…</option>
                {rangeValues(catalog.ranges.birthYear).map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <ErrorText field={F.birthYear} errors={list} />
            </div>
            <div className="field">
              <label htmlFor="pd">Pupillary distance (mm)</label>
              <select
                id="pd"
                value={form.pupillary_distance_mm ?? ''}
                onChange={(e) => set({ pupillary_distance_mm: num(e.target.value) })}
              >
                <option value="">Choose…</option>
                {rangeValues(catalog.ranges.pupillaryDistanceMm).map((pd) => (
                  <option key={pd} value={pd}>
                    {pd}
                  </option>
                ))}
              </select>
              <ErrorText field={F.pupillaryDistance} errors={list} />
            </div>
          </div>
          <fieldset className="field">
            <legend>Prescription uploaded to the EMR?</legend>
            <div className="chip-group">
              <label className="chip">
                <input
                  id="emr-yes"
                  type="radio"
                  name="emr"
                  checked={form.rx_uploaded_to_emr === true}
                  onChange={() => set({ rx_uploaded_to_emr: true })}
                />{' '}
                Yes
              </label>
              <label className="chip">
                <input
                  id="emr-no"
                  type="radio"
                  name="emr"
                  checked={form.rx_uploaded_to_emr === false}
                  onChange={() => set({ rx_uploaded_to_emr: false })}
                />{' '}
                No
              </label>
            </div>
            <ErrorText field={F.rxUploadedToEmr} errors={list} />
          </fieldset>
        </Card>

        <Card icon="eyeglasses" title="Frames">
          <p className="hint">Pick a color for each frame to order; that ticks the frame.</p>
          <ErrorText field={F.frames} errors={list} />
          <FramePicker
            frames={catalog.frames}
            selected={form.frames}
            errors={list}
            onToggle={(id, on) => setForm((f) => toggleFrame(f, id, on))}
            onPick={(id, color) => setForm((f) => toggleColor(f, id, color))}
          />

          <h3>Clip-on sunglasses (optional)</h3>
          <div className="clip-ons">
            <label className="chip clip-on">
              <input
                type="radio"
                name="clip-on"
                checked={form.clip_on_id === null}
                onChange={() => set({ clip_on_id: null })}
              />{' '}
              None
            </label>
            {catalog.clipOns.map((clip) => (
              <label key={clip.id} className="chip clip-on">
                <input
                  type="radio"
                  name="clip-on"
                  value={clip.id}
                  checked={form.clip_on_id === clip.id}
                  onChange={() => set({ clip_on_id: clip.id })}
                />
                <img src={clip.photo} alt="" /> {clip.name}
              </label>
            ))}
          </div>
          <ErrorText field={F.clipOn} errors={list} />
        </Card>

        <Card icon="visibility" title="Prescription">
          <div className="rx-scroll">
            <table className="rx">
              <thead>
                <tr>
                  <th></th>
                  <th>Sphere</th>
                  <th>Cylinder</th>
                  <th>Axis</th>
                  <th>Prism</th>
                  <th>Base</th>
                </tr>
              </thead>
              <tbody>
                <RxRow eye="od" label="OD (right eye)" rx={form.od} ranges={catalog.ranges} onChange={(od) => set({ od })} />
                <RxRow eye="os" label="OS (left eye)" rx={form.os} ranges={catalog.ranges} onChange={(os) => set({ os })} />
              </tbody>
            </table>
          </div>
          {eyeErrors(list).map((e) => (
            <div key={e.field + e.message} className="field-error" role="alert" data-error={e.field}>
              {e.message}
            </div>
          ))}
          <p className="hint">Cylinder is negative for both eyes or positive for both, never one of each.</p>

          <div className="field add-field">
            <label htmlFor="add">ADD (both eyes)</label>
            <select
              id="add"
              value={form.add === null ? '' : String(form.add)}
              onChange={(e) => set({ add: num(e.target.value) })}
            >
              <option value="">None</option>
              {rangeValues(catalog.ranges.add).map((v) => (
                <option key={v} value={String(v)}>
                  {signed(v)}
                </option>
              ))}
            </select>
            <p className="hint">ADD is always positive and makes the lenses bifocal or progressive.</p>
            <ErrorText field={F.add} errors={list} />
          </div>

          <fieldset className="field">
            <legend>Lens type</legend>
            <div className="chip-group">
              <label className="chip">
                <input type="radio" name="lens" checked={form.lens_type === null} onChange={() => set({ lens_type: null })} />{' '}
                {lensLabel(null)}
              </label>
              {OFFERED_LENS_TYPES.map((t) => (
                <label key={t} className="chip">
                  <input
                    id={`lens-${t.toLowerCase()}`}
                    type="radio"
                    name="lens"
                    checked={form.lens_type === t}
                    onChange={() => set({ lens_type: t })}
                  />{' '}
                  {lensLabel(t)}
                </label>
              ))}
            </div>
            <ErrorText field={F.lensType} errors={list} />
          </fieldset>
        </Card>

        <div className="actions">
          {errors !== null && errors.length > 0 && (
            <p className="form-error" role="alert">
              Fix the highlighted fields, then submit again.
            </p>
          )}
          {outcome !== null && 'saveFailed' in outcome && (
            <p className="form-error" role="alert" data-error="Save">
              Couldn't save the order — nothing was sent. Try again.
            </p>
          )}
          {outcome !== null && 'rejected' in outcome && (
            <p className="form-error" role="alert" data-error="Rejected">
              {outcome.rejected}
            </p>
          )}
          {outcome !== null && 'sessionExpired' in outcome && (
            <>
              <p className="form-error" role="alert" data-error="Session">
                Your session has expired. Sign in again to submit — your form will be kept.
              </p>
              <button type="button" className="btn tonal" data-icon="login" onClick={() => void signInAgain()}>
                Sign in
              </button>
            </>
          )}
          <button type="submit" className="btn filled" data-icon="send" disabled={busy}>
            {busy ? 'Submitting…' : 'Submit order'}
          </button>
        </div>
      </form>
    </>
  )
}
